import { SettingsBackupRestore } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { DocumentsAvailableForConfirming } from 'lib/api/confirming/types';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import {
  calculateDays,
  getRealDaysToCount,
  isDateAWeekDay,
} from 'lib/functions/dates';
import {
  formatDate,
  formatMoney,
  formatMoneyBack,
  hasAnyPermissions,
  showAlert,
} from 'lib/functions/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../../../../../../services/redux/authSlice';

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%', // ensure the table fits its container
    },
    tableCell: {
      padding: '4px !important',
      maxWidth: '200px',
      maxHeight: '10px',
      overflow: 'hidden', // Ensure the content doesn't overflow.
      whiteSpace: 'nowrap', // Ensure the text doesn't wrap to the next line.
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    selectFullWidth: {
      width: '100%',
    },
    selectSmall: {
      width: '200px', // or any other small width
    },
  }),
);

const cellStyle = {
  maxWidth: '150px',
  overflow: 'hidden', // Ensure the content doesn't overflow.
  textOverflow: 'ellipsis', // If the text overflows, it will end with "..."
  whiteSpace: 'nowrap', // Ensure the text doesn't wrap to the next line.
  padding: '4px 16px',
};

const DocumentsTable = (props: any) => {
  const {
    selectedDocuments,
    setSelectedDocuments,
    canForcePaymentDate,
    minimumDays,
    maximumDays,
    operationDate,
  } = props;
  const classes = useStyles();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  // Functions to handle documents changes
  const dateValidators = (date: Date, rowIndex: number) => {
    if (canForcePaymentDate) return true;

    const actualDate = selectedDocuments[rowIndex].paymentDate;
    if (!isDateAWeekDay(date)) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento debe ser un día hábil',
      );
      handleChangeTempDate(formatDate(actualDate), rowIndex);
      return false;
    }
    const minimumDate = new Date(operationDate);
    minimumDate.setDate(minimumDate.getDate() + minimumDays);
    if (date.getTime() < minimumDate.getTime()) {
      showAlert(
        dispatch,
        'warning',
        `La fecha de vencimiento no puede ser menor o igual a ${minimumDays} días a partir de la fecha de operación`,
      );
      handleChangeTempDate(formatDate(actualDate), rowIndex);
      return false;
    }
    if (maximumDays && maximumDays > 0) {
      for (const document of selectedDocuments) {
        const globalMaximumDate = new Date(operationDate);
        globalMaximumDate.setDate(
          globalMaximumDate.getDate() + maximumDays - 1,
        );
        const issuedDateLimit = new Date(document.issuedDate);
        issuedDateLimit.setDate(issuedDateLimit.getDate() + maximumDays - 1);
        if (date.getTime() > issuedDateLimit.getTime()) {
          showAlert(
            dispatch,
            'warning',
            `La fecha de vencimiento no puede ser mayor o igual a ${maximumDays} días que la fecha de emisión.`,
          );
          return false;
        }
        if (date.getTime() > globalMaximumDate.getTime()) {
          showAlert(
            dispatch,
            'warning',
            `La fecha de vencimiento no puede ser mayor o igual a ${maximumDays} días a partir de la fecha de operación`,
          );
          return false;
        }
      }
    }

    const days = calculateDays(
      new Date(selectedDocuments[rowIndex].issuedDate),
      new Date(date),
    );
    if (days <= 0) {
      showAlert(
        dispatch,
        'warning',
        'La fecha de vencimiento no puede ser menor a la fecha de emisión',
      );
      return false;
    }
    return days;
  };

  const handleChangeTempDate = (value: any, rowIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === rowIndex) {
        return {
          ...doc,
          tempDueDate: value,
        };
      }
      return doc;
    });
    setSelectedDocuments(newSelectedDocuments);
  };

  const handleChangeDateNormal = (index: number) => {
    const value = selectedDocuments[index].tempDueDate;
    const date = new Date(value);

    const days = dateValidators(date, index);
    if (!days) {
      const backupDate = new Date(selectedDocuments[index].dueDate);
      handleChangeTempDate(formatDate(backupDate), index);
      return;
    }
    const newSelectedDocuments = selectedDocuments.map((doc: any, i: any) => {
      if (i === index) {
        return {
          ...doc,
          dueDate: date?.toISOString(),
          paymentDate: date,
          tempDueDate: formatDate(date),
          daysToCount: getRealDaysToCount(date, operationDate),
        };
      }
      return doc;
    });

    setSelectedDocuments(newSelectedDocuments);
  };

  const handleChangeDocumentAmount = (value: string, rowIndex: number) => {
    const newSelectedDocuments = selectedDocuments.map(
      (doc: DocumentsAvailableForConfirming, i: number) => {
        if (i === rowIndex) {
          const newAmount = formatMoneyBack(value);

          if (
            (doc.originalAmount || doc.amount) !== undefined &&
            newAmount > (doc.originalAmount || doc.amount)
          ) {
            showAlert(
              dispatch,
              'warning',
              `El monto no puede ser mayor al valor original del documento (${formatMoney(doc.originalAmount || doc.amount)})`,
            );
            return doc;
          }
          return {
            ...doc,
            originalAmount: doc.originalAmount || doc.amount,
            amount: newAmount,
            amountAvailable: newAmount,
          };
        }
        return doc;
      },
    );
    setSelectedDocuments(newSelectedDocuments);
  };

  useEffect(() => {
    const newSelectedDocuments = selectedDocuments.map((doc: any) => {
      return {
        ...doc,
        paymentDate: doc.dueDate,
        tempDueDate: formatDate(doc.dueDate),
        daysToCount:
          doc.dueDate && doc.dueDate !== ''
            ? getRealDaysToCount(doc.dueDate, operationDate)
            : '',
        isOld: doc.isOld,
        subDocuments:
          doc?.subDocuments?.map((subDoc: any) => ({
            ...subDoc,
            folio: subDoc.folio
              ? subDoc.folio.split('-')[1] || subDoc.folio
              : null,
            daysToCount:
              subDoc.dueDate && subDoc.dueDate !== ''
                ? getRealDaysToCount(subDoc.dueDate, operationDate)
                : '',
            isOld: subDoc.isOld,
            paymentDate: subDoc.dueDate,
            tempDueDate: formatDate(subDoc.dueDate),
          })) || [],
      };
    });
    setSelectedDocuments(newSelectedDocuments);
  }, []);

  const dispatch = useDispatch();

  return (
    <TableContainer className={classes.tableContainer}>
      <Table className={classes.table}>
        <TableHead style={{ background: '#efefef' }}>
          <TableCell style={cellStyle}>Folio</TableCell>
          <TableCell style={cellStyle}>Código Doc.</TableCell>
          <TableCell style={cellStyle}>Proveedor</TableCell>
          <TableCell style={cellStyle}>Monto total</TableCell>
          <TableCell style={cellStyle}>Fecha emisión</TableCell>
          <TableCell style={cellStyle}>Fecha vencimiento</TableCell>
          <TableCell style={cellStyle}>Días</TableCell>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {selectedDocuments.flatMap((row: any, rowIndex: number) => {
            const parentRow = (
              <TableRow key={rowIndex}>
                <TableCell style={cellStyle}>{row.folio}</TableCell>
                <TableCell style={cellStyle}>{row.code}</TableCell>
                <TableCell style={cellStyle}>{row.businessName}</TableCell>
                <TableCell style={cellStyle}>
                  <TextField
                    value={formatMoney(row.amount)}
                    inputProps={{ style: { textAlign: 'right' } }}
                    onChange={(e) =>
                      handleChangeDocumentAmount(e.target.value, rowIndex)
                    }
                    disabled={
                      !hasAnyPermissions(user, [
                        'financing_factoring_edit_documents_amount',
                      ])
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LightTooltip
                            title={
                              <Typography style={{ padding: '8px' }}>
                                Disminuir monto por nota de crédito.
                              </Typography>
                            }
                            placement="top"
                          >
                            <span>
                              <IconButton
                                color="warning"
                                style={{ cursor: 'pointer' }}
                                disabled={
                                  row.creditNoteAmount == null ||
                                  row.creditNoteAmount <= 0 ||
                                  row.creditNoteAmount > row.amount ||
                                  row._creditNoteApplied ||
                                  !hasAnyPermissions(user, [
                                    'financing_factoring_edit_documents_amount',
                                  ])
                                }
                                onClick={() => {
                                  if (
                                    row.creditNoteAmount &&
                                    row.creditNoteAmount > 0 &&
                                    row.creditNoteAmount <= row.amount &&
                                    !row._creditNoteApplied
                                  ) {
                                    const newSelectedDocuments =
                                      selectedDocuments.map(
                                        (
                                          doc: DocumentsAvailableForConfirming,
                                          i: number,
                                        ) => {
                                          if (i === rowIndex) {
                                            return {
                                              ...doc,
                                              amount:
                                                doc.amount -
                                                (doc.creditNoteAmount || 0),
                                              amountAvailable:
                                                doc.amount -
                                                (doc.creditNoteAmount || 0),
                                              _creditNoteApplied: true,
                                            };
                                          }
                                          return doc;
                                        },
                                      );
                                    setSelectedDocuments(newSelectedDocuments);
                                  }
                                }}
                              >
                                <SettingsBackupRestore />
                              </IconButton>
                            </span>
                          </LightTooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
                <TableCell style={cellStyle}>
                  {formatDate(row.issuedDate)}
                </TableCell>
                <TableCell style={cellStyle}>
                  {
                    <TextField
                      value={row.tempDueDate}
                      type="date"
                      onChange={(e) =>
                        handleChangeTempDate(e.target.value, rowIndex)
                      }
                      onBlur={() => handleChangeDateNormal(rowIndex)}
                    />
                  }
                </TableCell>
                <TableCell style={cellStyle}>{row.daysToCount}</TableCell>
              </TableRow>
            );
            return [parentRow];
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DocumentsTable;
